import TradePicker from './TradePicker';
import { COLORS, STATUS_COLORS } from '../../styles/design-tokens';
import { DA_CATEGORIES } from '../../lib/daCategories';
import { PRICE_RANGE_DEFS } from '../../lib/constants';
import { activeCouncils } from '../../config/registry';
import { useIsMobile } from '../../hooks/useIsMobile';

const COUNCILS = activeCouncils();
const STATUSES   = ["Lodged", "Under Assessment", "On Exhibition", "Approved", "Rejected"];
const APP_TYPES  = ["All", "DA", "CDC"];

export default function FilterDrawer({
  open, onClose,
  councils, togC, allC,
  stFilt, togSt, allStFilt,
  selectedCategories, togCat, allCat,
  selectedPriceRanges, togPriceRange, clearPriceRanges,
  appTypeFilter, setAppTypeFilter,
  selectedTrade = '', onTradeChange = () => {},
  resultCount,
  isDesktopLayout,
}) {
  const isNarrow = useIsMobile(900);
  const isDesktop = typeof isDesktopLayout === 'boolean' ? isDesktopLayout : !isNarrow;
  const safeRanges = Array.isArray(selectedPriceRanges) ? selectedPriceRanges : [];
  const safeCats   = Array.isArray(selectedCategories)  ? selectedCategories  : [];

  const activeCount =
    (councils.length < COUNCILS.length ? 1 : 0) +
    (stFilt.length < STATUSES.length ? 1 : 0) +
    (selectedTrade ? 1 : safeCats.length < DA_CATEGORIES.length ? 1 : 0) +
    safeRanges.length +
    (appTypeFilter && appTypeFilter !== 'All' ? 1 : 0);

  const handleClearAll = () => {
    onTradeChange('');
    allC();
    if (allStFilt) allStFilt();
    if (allCat) allCat();
    if (clearPriceRanges) clearPriceRanges();
    if (setAppTypeFilter) setAppTypeFilter('All');
  };

  const activeSummary = activeCount > 0 ? `${activeCount} active` : 'All loaded applications';

  const Pill = ({ active, color, onClick, children }) => (
    <button type="button" aria-pressed={active} onClick={onClick} style={{
      background: active ? `linear-gradient(180deg, ${(color || COLORS.amber)}24, ${(color || COLORS.amber)}12)` : "rgba(10,27,54,0.72)",
      border: `1px solid ${active ? (color || COLORS.amber) : 'rgba(168,184,204,0.16)'}`,
      color: active ? (color || COLORS.amber) : COLORS.textMuted,
      borderRadius: 999, padding: "8px 12px", fontSize: 12, fontWeight: 700,
      cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
      display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
      minHeight: 34,
      boxSizing: "border-box",
      whiteSpace: "nowrap",
      transition: "background 0.16s ease, border-color 0.16s ease, color 0.16s ease, box-shadow 0.16s ease",
      boxShadow: active ? `0 0 0 1px ${(color || COLORS.amber)}18 inset` : 'none',
    }}>
      {children}
      <span aria-hidden="true" style={{ width: 10, fontSize: 10, lineHeight: 1, textAlign: "center", opacity: active ? 1 : 0 }}>✓</span>
    </button>
  );

  const Section = ({ title, hint, children }) => (
    <section style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, marginBottom: hint ? 6 : 10 }}>
        <div style={{ color: COLORS.textDim, fontSize: 10, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase" }}>{title}</div>
      </div>
      {hint && <div style={{ color: COLORS.textDim, fontSize: 11, lineHeight: 1.35, marginBottom: 10 }}>{hint}</div>}
      {children}
    </section>
  );

  return (
    <>
      {/* Overlay */}
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, background: "rgba(2,8,18,0.72)",
          zIndex: 999, opacity: open ? 1 : 0, pointerEvents: open ? "auto" : "none",
          transition: "opacity 0.2s",
          backdropFilter: open ? "blur(2px)" : "none",
        }}
      />
      {/* Drawer */}
      <div style={{
        position: "fixed",
        top: 0,
        right: isDesktop ? 0 : "max(0px, calc((100vw - 430px) / 2))",
        bottom: 0,
        width: isDesktop ? "min(38vw, 420px)" : "min(85vw, 340px)",
        minWidth: isDesktop ? 360 : undefined,
        maxWidth: isDesktop ? 420 : 340,
        background: "linear-gradient(180deg, #0b1829 0%, #071322 100%)", zIndex: 1000,
        transform: open ? "translateX(0)" : "translateX(100%)",
        transition: "transform 0.25s ease",
        overflowY: "auto", padding: "20px 16px 32px",
        borderLeft: `1px solid rgba(192,160,96,0.28)`,
        boxShadow: "-24px 0 60px rgba(0,0,0,0.38)",
        fontFamily: "'DM Sans', sans-serif",
      }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 24 }}>
          <div>
            <div style={{ color: COLORS.text, fontSize: 18, fontWeight: 850, letterSpacing: 0 }}>Filters</div>
            <div style={{ color: COLORS.textDim, fontSize: 12, marginTop: 3 }}>{activeSummary}</div>
          </div>
          <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
            {activeCount > 0 && (
              <button
                type="button"
                onClick={handleClearAll}
                style={{ background: "transparent", border: "none", color: COLORS.amber, fontSize: 12, fontWeight: 700, cursor: "pointer", padding: "8px 2px", fontFamily: "inherit", minHeight: 36 }}
              >Clear all</button>
            )}
            <button type="button" onClick={onClose} aria-label="Close filters" style={{
              width: 32, height: 32, borderRadius: 8, border: "1px solid rgba(168,184,204,0.18)",
              background: "rgba(10,27,54,0.72)", color: COLORS.textMuted, fontSize: 18, cursor: "pointer", lineHeight: 1,
            }}>×</button>
          </div>
        </div>

        <TradePicker selectedTrade={selectedTrade} onChange={onTradeChange} />

        {/* Council */}
        <Section title="Council">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {COUNCILS.map(c => (
              <Pill key={c.name} active={councils.includes(c.name)} onClick={() => togC(c.name)}>
                {c.shortName}
              </Pill>
            ))}
          </div>
        </Section>

        {/* Status */}
        <Section title="Status">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {STATUSES.map(s => {
              const sc = STATUS_COLORS[s] || { text: COLORS.textMuted };
              return (
                <Pill key={s} active={stFilt.includes(s)} color={sc.text} onClick={() => togSt(s)}>
                  <span style={{ width: 6, height: 6, borderRadius: 3, background: sc.dot || sc.text, flexShrink: 0 }} />
                  {s}
                </Pill>
              );
            })}
          </div>
        </Section>

        {/* Category */}
        <Section title="Project category" hint={selectedTrade ? "Trade matching covers all project categories. Clear your trade to use category filters." : undefined}>
          {!selectedTrade && <>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {DA_CATEGORIES.map(cat => (
              <Pill key={cat.id} active={safeCats.includes(cat.id)} onClick={() => togCat && togCat(cat.id)}>
                {cat.label}
              </Pill>
            ))}
          </div>
          </>}
        </Section>

        {/* Price Range */}
        <Section title="Price Range" hint="DAs without cost data are hidden when a price filter is active.">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {PRICE_RANGE_DEFS.map(r => (
              <Pill key={r.id} active={safeRanges.includes(r.id)} onClick={() => togPriceRange && togPriceRange(r.id)}>
                {r.label}
              </Pill>
            ))}
          </div>
        </Section>

        {/* Application Type */}
        <Section title="Application Type">
          <div style={{ display: "flex", gap: 8 }}>
            {APP_TYPES.map(t => {
              const active = (appTypeFilter || 'All') === t;
              return (
                <button key={t} onClick={() => setAppTypeFilter && setAppTypeFilter(t)} style={{
                  background: active ? COLORS.amber + "22" : "rgba(10,27,54,0.72)",
                  border: `1px solid ${active ? COLORS.amber : 'rgba(168,184,204,0.16)'}`,
                  color: active ? COLORS.amber : COLORS.textMuted,
                  borderRadius: 999, padding: "8px 14px", fontSize: 12, fontWeight: 700,
                  cursor: "pointer", fontFamily: "inherit", minHeight: 34, boxSizing: "border-box", whiteSpace: "nowrap",
                  transition: "background 0.16s ease, border-color 0.16s ease, color 0.16s ease",
                }}>
                  {t}
                </button>
              );
            })}
          </div>
        </Section>

        <button
          onClick={onClose}
          style={{
            width: "100%", background: `linear-gradient(145deg, ${COLORS.amber}, ${COLORS.amberDark}, #a89050)`, border: "none",
            color: COLORS.bg, borderRadius: 8, padding: "14px 16px",
            fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
            marginTop: 4, boxShadow: "0 10px 28px rgba(192,160,96,0.18)",
          }}
        >
          {resultCount !== undefined ? `Show ${resultCount} loaded results` : "Apply Filters"}
        </button>
      </div>
    </>
  );
}
